import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";

interface SSOConfig {
  id?: string;
  provider_type: "saml" | "oidc";
  provider_name: string;
  domain: string;
  saml_sso_url?: string;
  saml_issuer?: string;
  saml_certificate?: string;
  oidc_client_id?: string;
  oidc_client_secret?: string;
  oidc_discovery_url?: string;
  attribute_mapping: {
    email?: string;
    name?: string;
  };
  auto_provision: boolean;
  enabled: boolean;
}

const providers = [
  {
    id: "azure-ad",
    name: "Microsoft Azure AD / Entra ID",
    type: "saml" as const,
  },
  { id: "okta", name: "Okta", type: "saml" as const },
  {
    id: "google-workspace",
    name: "Google Workspace",
    type: "oidc" as const,
  },
  {
    id: "custom-saml",
    name: "Custom SAML Provider",
    type: "saml" as const,
  },
];

// Provider-specific attribute mapping defaults
const providerDefaults: Record<
  string,
  {
    attribute_mapping: SSOConfig["attribute_mapping"];
    oidc_discovery_url?: string;
  }
> = {
  "azure-ad": {
    attribute_mapping: {
      email: "userprincipalname",
      name: "givenname,surname",
    },
  },
  okta: {
    attribute_mapping: {
      email: "email",
      name: "firstName,lastName",
    },
  },
  "google-workspace": {
    attribute_mapping: {
      email: "email",
      name: "name",
    },
    oidc_discovery_url: "https://accounts.google.com",
  },
  "custom-saml": {
    attribute_mapping: {
      email: "email",
      name: "displayName",
    },
  },
};

const SSOConfiguration: React.FC = () => {
  const [config, setConfig] = useState<SSOConfig>({
    provider_type: "saml",
    provider_name: "",
    domain: "",
    saml_sso_url: "",
    saml_issuer: "",
    saml_certificate: "",
    oidc_client_id: "",
    oidc_client_secret: "",
    oidc_discovery_url: "",
    attribute_mapping: {
      email: "email",
      name: "displayName",
    },
    auto_provision: true,
    enabled: false,
  });

  const [selectedProviderId, setSelectedProviderId] = useState<string>("");
  const [testResult, setTestResult] = useState<{
    success: boolean;
    error?: string;
    message?: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadExistingConfig();
  }, []);

  const loadExistingConfig = async () => {
    try {
      const response = await axios.get("/api/agency/sso-config");
      if (response.data.config) {
        setConfig(response.data.config);
        // Infer selected provider from saved config
        if (response.data.config.provider_name) {
          const match = providers.find(
            (p) => p.name === response.data.config.provider_name
          );
          if (match) {
            setSelectedProviderId(match.id);
          } else if (response.data.config.provider_type === "saml") {
            setSelectedProviderId("custom-saml");
          }
        }
      }
    } catch (error) {
      console.error("Failed to load SSO config:", error);
    }
  };

  const handleTest = async () => {
    setLoading(true);
    setTestResult(null);

    try {
      const configToTest = { ...config };

      // If certificate is masked, ask backend to test with stored certificate
      if (configToTest.saml_certificate === "***certificate_set***") {
        configToTest.saml_certificate = "__USE_STORED_CERTIFICATE__";
      }
      // If OIDC secret is masked, ask backend to test with stored secret
      if (configToTest.oidc_client_secret === "***masked***") {
        configToTest.oidc_client_secret = "__USE_STORED_SECRET__";
      }

      const response = await axios.post(
        "/api/agency/sso-config/test",
        configToTest
      );
      setTestResult(response.data);
    } catch (error: any) {
      setTestResult({
        success: false,
        error: error.response?.data?.error || "Test failed",
      });
    }

    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      const configToSave = { ...config };

      // Don't send masked values - let backend keep existing values
      if (configToSave.saml_certificate === "***certificate_set***") {
        delete configToSave.saml_certificate;
      }
      if (configToSave.oidc_client_secret === "***masked***") {
        delete configToSave.oidc_client_secret;
      }

      await axios.post("/api/agency/sso-config", configToSave);
      toast.success("SSO configuration saved successfully!");
      await loadExistingConfig();
    } catch (error: any) {
      toast.error(
        "Failed to save SSO configuration: " +
          (error.response?.data?.error || error.message)
      );
    }

    setSaving(false);
  };

  const handleProviderSelect = (providerId: string) => {
    const provider = providers.find((p) => p.id === providerId);
    if (provider) {
      setSelectedProviderId(providerId);
      const defaults = providerDefaults[providerId];

      setConfig((prev) => ({
        ...prev,
        provider_type: provider.type,
        provider_name: provider.id === "custom-saml" ? "" : provider.name,
        attribute_mapping: defaults?.attribute_mapping || prev.attribute_mapping,
        ...(defaults?.oidc_discovery_url
          ? { oidc_discovery_url: defaults.oidc_discovery_url }
          : {}),
      }));
    }
  };

  const renderSetupInstructions = () => {
    switch (selectedProviderId) {
      case "azure-ad":
        return (
          <div className="bg-blue-50 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800 rounded-md p-4">
            <h4 className="font-medium text-blue-900 dark:text-blue-300 mb-2">
              Azure AD / Entra ID Setup Instructions
            </h4>
            <ol className="space-y-2 text-sm text-blue-900 dark:text-blue-300 list-decimal pl-4">
              <li>
                Go to Azure Portal &rarr; Entra ID &rarr; Enterprise
                Applications &rarr; New Application
              </li>
              <li>
                Select &quot;Create your own application&quot; &rarr;
                &quot;Non-gallery application&quot;
              </li>
              <li>Go to Single sign-on &rarr; select SAML</li>
              <li>
                In Basic SAML Configuration, set:
                <ul className="list-disc pl-4 mt-1 space-y-1">
                  <li>
                    <strong>Identifier (Entity ID):</strong>{" "}
                    <code className="bg-base-100 px-1 rounded text-xs">
                      police-narratives-ai
                    </code>
                  </li>
                  <li>
                    <strong>Reply URL (ACS URL):</strong>{" "}
                    <code className="bg-base-100 px-1 rounded text-xs">
                      https://app.policenarratives.ai/api/sso/saml/callback
                    </code>
                  </li>
                </ul>
              </li>
              <li>
                In Attributes &amp; Claims, verify the default claims are
                present:
                <ul className="list-disc pl-4 mt-1 space-y-1">
                  <li>
                    <code className="text-xs">emailaddress</code> &rarr;
                    user.mail
                  </li>
                  <li>
                    <code className="text-xs">givenname</code> &rarr;
                    user.givenname
                  </li>
                  <li>
                    <code className="text-xs">surname</code> &rarr;
                    user.surname
                  </li>
                </ul>
              </li>
              <li>
                Download the <strong>Certificate (Base64)</strong> from the SAML
                Signing Certificate section
              </li>
              <li>
                Copy the <strong>Login URL</strong> and{" "}
                <strong>Microsoft Entra Identifier</strong> (also called Azure
                AD Identifier) from section 4, &quot;Set up&quot;
              </li>
            </ol>
            <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800 text-sm text-blue-900 dark:text-blue-300">
              <p className="font-medium mb-1">
                Then paste into the fields above:
              </p>
              <ul className="list-disc pl-4 space-y-1">
                <li>
                  <strong>Login URL</strong> &rarr; SSO URL field
                </li>
                <li>
                  <strong>Microsoft Entra Identifier</strong> &rarr; Issuer
                  (Entity ID) field — both contain your tenant ID, and it must
                  be the same in both
                </li>
                <li>
                  <strong>Certificate (Base64)</strong> file contents &rarr;
                  X.509 Certificate field
                </li>
              </ul>
            </div>
            <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800 space-y-1 text-sm">
              <div>
                <strong>ACS URL:</strong>{" "}
                <code className="bg-base-100 px-2 py-1 rounded text-xs">
                  https://app.policenarratives.ai/api/sso/saml/callback
                </code>
              </div>
              <div>
                <strong>Entity ID:</strong>{" "}
                <code className="bg-base-100 px-2 py-1 rounded text-xs">
                  police-narratives-ai
                </code>
              </div>
              <div>
                <strong>Name ID Format:</strong>{" "}
                <code className="bg-base-100 px-2 py-1 rounded text-xs">
                  EmailAddress
                </code>
              </div>
            </div>
          </div>
        );

      case "okta":
        return (
          <div className="bg-blue-50 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800 rounded-md p-4">
            <h4 className="font-medium text-blue-900 dark:text-blue-300 mb-2">
              Okta Setup Instructions
            </h4>
            <ol className="space-y-2 text-sm text-blue-900 dark:text-blue-300 list-decimal pl-4">
              <li>
                Go to Okta Admin &rarr; Applications &rarr; Create App
                Integration
              </li>
              <li>Select SAML 2.0 and click Next</li>
              <li>
                In SAML Settings, set:
                <ul className="list-disc pl-4 mt-1 space-y-1">
                  <li>
                    <strong>Single sign-on URL:</strong>{" "}
                    <code className="bg-base-100 px-1 rounded text-xs">
                      https://app.policenarratives.ai/api/sso/saml/callback
                    </code>
                  </li>
                  <li>
                    <strong>Audience URI (SP Entity ID):</strong>{" "}
                    <code className="bg-base-100 px-1 rounded text-xs">
                      police-narratives-ai
                    </code>
                  </li>
                  <li>
                    <strong>Name ID format:</strong> EmailAddress
                  </li>
                </ul>
              </li>
              <li>
                Add Attribute Statements:
                <ul className="list-disc pl-4 mt-1 space-y-1">
                  <li>
                    <code className="text-xs">email</code> &rarr; user.email
                  </li>
                  <li>
                    <code className="text-xs">firstName</code> &rarr;
                    user.firstName
                  </li>
                  <li>
                    <code className="text-xs">lastName</code> &rarr;
                    user.lastName
                  </li>
                </ul>
              </li>
              <li>Complete setup, then go to the Sign On tab</li>
              <li>
                Copy the <strong>Identity Provider Issuer</strong>,{" "}
                <strong>SSO URL</strong>, and download the{" "}
                <strong>X.509 Certificate</strong>
              </li>
            </ol>
            <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800 space-y-1 text-sm">
              <div>
                <strong>ACS URL:</strong>{" "}
                <code className="bg-base-100 px-2 py-1 rounded text-xs">
                  https://app.policenarratives.ai/api/sso/saml/callback
                </code>
              </div>
              <div>
                <strong>Entity ID:</strong>{" "}
                <code className="bg-base-100 px-2 py-1 rounded text-xs">
                  police-narratives-ai
                </code>
              </div>
              <div>
                <strong>Name ID Format:</strong>{" "}
                <code className="bg-base-100 px-2 py-1 rounded text-xs">
                  EmailAddress
                </code>
              </div>
            </div>
          </div>
        );

      case "google-workspace":
        return (
          <div className="bg-blue-50 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800 rounded-md p-4">
            <h4 className="font-medium text-blue-900 dark:text-blue-300 mb-2">
              Google Workspace Setup Instructions
            </h4>
            <ol className="space-y-2 text-sm text-blue-900 dark:text-blue-300 list-decimal pl-4">
              <li>
                Go to{" "}
                <strong>
                  Google Cloud Console &rarr; APIs &amp; Services &rarr;
                  Credentials
                </strong>
              </li>
              <li>
                Create an <strong>OAuth 2.0 Client ID</strong> (Web application
                type)
              </li>
              <li>
                Add Authorized redirect URI:
                <ul className="list-disc pl-4 mt-1">
                  <li>
                    <code className="bg-base-100 px-1 rounded text-xs">
                      https://app.policenarratives.ai/api/sso/oidc/callback
                    </code>
                  </li>
                </ul>
              </li>
              <li>
                Copy the <strong>Client ID</strong> and{" "}
                <strong>Client Secret</strong>
              </li>
              <li>
                The Discovery URL is pre-filled:{" "}
                <code className="text-xs">https://accounts.google.com</code>
              </li>
              <li>
                (Optional) To send Badge ID, configure custom attributes in
                Google Admin &rarr; Directory &rarr; Users &rarr; Custom
                attributes
              </li>
            </ol>
            <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800 space-y-1 text-sm">
              <div>
                <strong>Redirect URI:</strong>{" "}
                <code className="bg-base-100 px-2 py-1 rounded text-xs">
                  https://app.policenarratives.ai/api/sso/oidc/callback
                </code>
              </div>
            </div>
          </div>
        );

      case "custom-saml":
        return (
          <div className="bg-blue-50 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800 rounded-md p-4">
            <h4 className="font-medium text-blue-900 dark:text-blue-300 mb-2">
              Configure these URLs in your SAML provider:
            </h4>
            <div className="space-y-1 text-sm">
              <div>
                <strong>ACS URL:</strong>{" "}
                <code className="bg-base-100 px-2 py-1 rounded text-xs">
                  https://app.policenarratives.ai/api/sso/saml/callback
                </code>
              </div>
              <div>
                <strong>Entity ID:</strong>{" "}
                <code className="bg-base-100 px-2 py-1 rounded text-xs">
                  police-narratives-ai
                </code>
              </div>
              <div>
                <strong>Name ID Format:</strong>{" "}
                <code className="bg-base-100 px-2 py-1 rounded text-xs">
                  EmailAddress
                </code>
              </div>
              <div>
                <strong>SSO Login URL:</strong>{" "}
                <code className="bg-base-100 px-2 py-1 rounded text-xs">
                  https://app.policenarratives.ai/api/sso/saml/
                  {config.domain || "{domain}"}/login
                </code>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const isTestDisabled =
    loading ||
    !config.domain ||
    (!config.provider_name && selectedProviderId === "custom-saml") ||
    (config.provider_type === "saml" && !config.saml_sso_url) ||
    (config.provider_type === "oidc" && !config.oidc_client_id);

  const isSaveDisabled =
    saving ||
    !config.domain ||
    !selectedProviderId ||
    (!config.provider_name && selectedProviderId === "custom-saml");

  return (
    <div className="p-6 card border border-base-300 bg-base-100 mt-8">
      <h2 className="text-2xl font-semibold text-base-content mb-2">
        Single Sign-On Configuration
      </h2>
      <p className="text-base-content/60 mb-6">
        Configure SSO for your agency to allow officers to login with your
        existing identity provider.
      </p>

      <div className="space-y-6">
        {/* Provider Selection */}
        <div>
          <label className="text-gray-800 dark:text-base-content/80 font-medium text-xs md:text-base pb-3 block">
            Choose Your Identity Provider
          </label>
          <div className="grid grid-cols-2 gap-4">
            {providers.map((provider) => (
              <div
                key={provider.id}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                  selectedProviderId === provider.id
                    ? "border-blue-500 bg-blue-50 dark:bg-blue-900/40"
                    : "border-base-300 hover:border-gray-300 dark:hover:border-base-content/30"
                }`}
                onClick={() => handleProviderSelect(provider.id)}
              >
                <h3 className="font-medium text-base-content">{provider.name}</h3>
                <span className="text-xs text-base-content/60 uppercase">
                  {provider.type}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Basic Configuration */}
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="text-gray-800 dark:text-base-content/80 font-medium text-xs md:text-base pb-2 block">
              Email Domain
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 dark:border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., yourcompany.com"
              value={config.domain}
              onChange={(e) => setConfig({ ...config, domain: e.target.value })}
            />
            <p className="text-xs text-base-content/60 mt-1">
              Officers with this email domain will be redirected to SSO. Must
              be all lowercase, with no @ sign. Example:{" "}
              <code className="bg-gray-100 dark:bg-base-300 px-1 rounded">cityofexample.gov</code>
            </p>
          </div>
          {selectedProviderId === "custom-saml" && (
            <div>
              <label className="text-gray-800 dark:text-base-content/80 font-medium text-xs md:text-base pb-2 block">
                Provider Name
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 dark:border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., PingFederate, ADFS"
                value={config.provider_name}
                onChange={(e) =>
                  setConfig({ ...config, provider_name: e.target.value })
                }
              />
            </div>
          )}
        </div>

        {/* SAML Configuration */}
        {config.provider_type === "saml" && selectedProviderId && (
          <div className="space-y-6 p-6 bg-base-200 rounded-lg">
            <h3 className="text-lg font-medium text-base-content">
              SAML 2.0 Configuration
            </h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-base-content/80 mb-2">
                SSO URL (Login URL)
              </label>
              <input
                type="url"
                className="w-full px-3 py-2 border border-gray-300 dark:border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={
                  selectedProviderId === "azure-ad"
                    ? "https://login.microsoftonline.com/{tenant-id}/saml2"
                    : selectedProviderId === "okta"
                    ? "https://{your-okta-domain}/app/{app-id}/sso/saml"
                    : "https://your-idp.com/saml/sso"
                }
                value={config.saml_sso_url}
                onChange={(e) =>
                  setConfig({ ...config, saml_sso_url: e.target.value })
                }
              />
              <p className="text-xs text-base-content/60 mt-1">
                {selectedProviderId === "azure-ad" ? (
                  <>
                    Copy the <strong>Login URL</strong> from your enterprise
                    application (Single sign-on &rarr; section 4 &quot;Set
                    up&quot;). Example:{" "}
                    <code className="bg-gray-100 dark:bg-base-300 px-1 rounded">
                      https://login.microsoftonline.com/a1b2c3d4-e5f6-7890-abcd-1234567890ef/saml2
                    </code>
                  </>
                ) : selectedProviderId === "okta" ? (
                  <>
                    Copy the <strong>Sign on URL</strong> from your Okta app
                    (Sign On tab &rarr; View SAML setup instructions). Example:{" "}
                    <code className="bg-gray-100 dark:bg-base-300 px-1 rounded">
                      https://youragency.okta.com/app/abc123def456/sso/saml
                    </code>
                  </>
                ) : (
                  <>
                    The URL your identity provider uses to receive SAML login
                    requests. Example:{" "}
                    <code className="bg-gray-100 dark:bg-base-300 px-1 rounded">
                      https://your-idp.com/saml/sso
                    </code>
                  </>
                )}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-base-content/80 mb-2">
                Issuer (Entity ID)
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 dark:border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={
                  selectedProviderId === "azure-ad"
                    ? "https://sts.windows.net/{tenant-id}/"
                    : selectedProviderId === "okta"
                    ? "http://www.okta.com/{okta-entity-id}"
                    : "https://your-idp.com/entity-id"
                }
                value={config.saml_issuer}
                onChange={(e) =>
                  setConfig({ ...config, saml_issuer: e.target.value })
                }
              />
              <p className="text-xs text-base-content/60 mt-1">
                {selectedProviderId === "azure-ad" ? (
                  <>
                    Copy the <strong>Microsoft Entra Identifier</strong> (also
                    called Azure AD Identifier) from the same &quot;Set
                    up&quot; section as the Login URL — do not type it by hand.
                    Example:{" "}
                    <code className="bg-gray-100 dark:bg-base-300 px-1 rounded">
                      https://sts.windows.net/a1b2c3d4-e5f6-7890-abcd-1234567890ef/
                    </code>
                  </>
                ) : selectedProviderId === "okta" ? (
                  <>
                    Copy the <strong>Issuer</strong> from your Okta app (Sign
                    On tab &rarr; View SAML setup instructions). Example:{" "}
                    <code className="bg-gray-100 dark:bg-base-300 px-1 rounded">
                      http://www.okta.com/abc123def456
                    </code>
                  </>
                ) : (
                  <>
                    The exact issuer value your identity provider includes in
                    its SAML responses. Example:{" "}
                    <code className="bg-gray-100 dark:bg-base-300 px-1 rounded">
                      https://your-idp.com/entity-id
                    </code>
                  </>
                )}
              </p>
              {selectedProviderId === "azure-ad" && (
                <p className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 rounded p-2 mt-2">
                  <strong>Common mistake:</strong> this value must be a full
                  URL, include the trailing slash, and contain the{" "}
                  <strong>same tenant ID</strong> as your Login URL above. A
                  wrong or mismatched tenant ID causes a &quot;config not
                  found&quot; error after officers sign in at Microsoft.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-base-content/80 mb-2">
                X.509 Certificate
              </label>
              {config.saml_certificate === "***certificate_set***" ? (
                <div className="space-y-2">
                  <div className="w-full px-3 py-2 border border-green-300 dark:border-green-900 rounded-md bg-green-50 dark:bg-green-950/40 text-green-800 dark:text-green-400 text-sm">
                    Certificate is configured and saved
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setConfig({ ...config, saml_certificate: "" })
                    }
                    className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-200 underline"
                  >
                    Replace certificate
                  </button>
                </div>
              ) : (
                <textarea
                  className="w-full h-32 px-3 py-2 border border-gray-300 dark:border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-xs"
                  placeholder={
                    "-----BEGIN CERTIFICATE-----\nMIICXjCCAcegAwIBAgIJAJ...\n-----END CERTIFICATE-----"
                  }
                  value={config.saml_certificate}
                  onChange={(e) =>
                    setConfig({ ...config, saml_certificate: e.target.value })
                  }
                />
              )}
              <p className="text-xs text-base-content/60 mt-1">
                Copy the X.509 certificate from your SAML provider (including
                BEGIN/END lines)
              </p>
            </div>

            {renderSetupInstructions()}
          </div>
        )}

        {/* OIDC Configuration */}
        {config.provider_type === "oidc" && selectedProviderId && (
          <div className="space-y-6 p-6 bg-base-200 rounded-lg">
            <h3 className="text-lg font-medium text-base-content">
              OpenID Connect Configuration
            </h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-base-content/80 mb-2">
                Client ID
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 dark:border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={
                  selectedProviderId === "google-workspace"
                    ? "your-client-id.apps.googleusercontent.com"
                    : "your-client-id"
                }
                value={config.oidc_client_id}
                onChange={(e) =>
                  setConfig({ ...config, oidc_client_id: e.target.value })
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-base-content/80 mb-2">
                Client Secret
              </label>
              {config.oidc_client_secret === "***masked***" ? (
                <div className="space-y-2">
                  <div className="w-full px-3 py-2 border border-green-300 dark:border-green-900 rounded-md bg-green-50 dark:bg-green-950/40 text-green-800 dark:text-green-400 text-sm">
                    Client secret is configured and saved
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setConfig({ ...config, oidc_client_secret: "" })
                    }
                    className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-200 underline"
                  >
                    Replace client secret
                  </button>
                </div>
              ) : (
                <input
                  type="password"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={
                    selectedProviderId === "google-workspace"
                      ? "GOCSPX-..."
                      : "your-client-secret"
                  }
                  value={config.oidc_client_secret}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      oidc_client_secret: e.target.value,
                    })
                  }
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-base-content/80 mb-2">
                Discovery URL (Issuer URL)
              </label>
              <input
                type="url"
                className="w-full px-3 py-2 border border-gray-300 dark:border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="https://accounts.google.com"
                value={config.oidc_discovery_url}
                onChange={(e) =>
                  setConfig({ ...config, oidc_discovery_url: e.target.value })
                }
              />
              <p className="text-xs text-base-content/60 mt-1">
                The OpenID Connect issuer URL (the system appends
                /.well-known/openid-configuration automatically)
              </p>
            </div>

            {renderSetupInstructions()}
          </div>
        )}

        {/* Attribute Mapping */}
        {selectedProviderId && (
          <div className="space-y-4 p-6 bg-base-200 rounded-lg">
            <h3 className="text-lg font-medium text-base-content">
              Attribute Mapping
            </h3>
            <p className="text-sm text-gray-600 dark:text-base-content/70">
              Map your SSO attributes to Police Narratives user fields
            </p>

            {config.provider_type === "oidc" && (
              <div className="text-sm text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800 rounded p-3">
                OpenID Connect uses standard claims (email, name, sub)
                automatically. Only customize these if your provider uses
                non-standard claim names.
              </div>
            )}

            {config.provider_type === "saml" && (
              <div className="text-sm text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800 rounded p-3">
                Use short attribute names like{" "}
                <code className="bg-base-100 px-1 rounded text-xs">
                  userprincipalname
                </code>
                , not full claim URLs like{" "}
                <code className="bg-base-100 px-1 rounded text-xs">
                  http://schemas.xmlsoap.org/...
                </code>
                . Watch for typos — the attribute name must match exactly.
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-base-content/80 mb-2">
                  Email Attribute
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={
                    config.provider_type === "oidc"
                      ? "email"
                      : "userprincipalname"
                  }
                  value={config.attribute_mapping.email}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      attribute_mapping: {
                        ...config.attribute_mapping,
                        email: e.target.value,
                      },
                    })
                  }
                />
                <p className="text-xs text-base-content/60 mt-1">
                  The attribute holding the officer&apos;s email address.
                  Example:{" "}
                  <code className="bg-gray-100 dark:bg-base-300 px-1 rounded">
                    {selectedProviderId === "azure-ad"
                      ? "userprincipalname"
                      : "email"}
                  </code>
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-base-content/80 mb-2">
                  Name Attribute{" "}
                  <span className="text-xs text-base-content/60 mt-1">
                    (Use single field or comma-separated fields to combine)
                  </span>
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={
                    config.provider_type === "oidc"
                      ? "name (or given_name,family_name)"
                      : "displayName (or givenname,surname)"
                  }
                  value={config.attribute_mapping.name}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      attribute_mapping: {
                        ...config.attribute_mapping,
                        name: e.target.value,
                      },
                    })
                  }
                />
                <p className="text-xs text-base-content/60 mt-1">
                  Comma-separated attributes are combined with a space.
                  Example:{" "}
                  <code className="bg-gray-100 dark:bg-base-300 px-1 rounded">
                    {selectedProviderId === "azure-ad"
                      ? "givenname,surname"
                      : selectedProviderId === "okta"
                      ? "firstName,lastName"
                      : config.provider_type === "oidc"
                      ? "name"
                      : "displayName"}
                  </code>{" "}
                  {selectedProviderId === "azure-ad" &&
                    'produces "John Smith" from first and last name.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Settings */}
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-base-200 rounded-lg">
            <div>
              <h4 className="font-medium text-base-content">
                Auto-provision Users
              </h4>
              <p className="text-sm text-gray-600 dark:text-base-content/70">
                Automatically create user accounts when officers login for the
                first time
              </p>
            </div>
            <input
              type="checkbox"
              className="toggle toggle-md border-2 border-gray-400 bg-gray-400 checked:border-blue-500 checked:bg-blue-500 [&:not(:checked)]:bg-gray-400 [&:after]:!bg-white"
              checked={config.auto_provision}
              onChange={(e) =>
                setConfig({ ...config, auto_provision: e.target.checked })
              }
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-base-200 rounded-lg">
            <div>
              <h4 className="font-medium text-base-content">Enable SSO</h4>
              <p className="text-sm text-gray-600 dark:text-base-content/70">
                Officers with your domain will be redirected to SSO login
              </p>
            </div>
            <input
              type="checkbox"
              className="toggle toggle-md border-2 border-gray-400 bg-gray-400 checked:border-blue-500 checked:bg-blue-500 [&:not(:checked)]:bg-gray-400 [&:after]:!bg-white"
              checked={config.enabled}
              onChange={(e) =>
                setConfig({ ...config, enabled: e.target.checked })
              }
            />
          </div>
        </div>

        {/* Test Results */}
        {testResult && (
          <div
            className={`p-4 rounded-md ${
              testResult.success
                ? "bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-900"
                : "bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900"
            }`}
          >
            {testResult.success ? (
              <span className="text-green-800 dark:text-green-400">
                {testResult.message || "SSO configuration test successful!"}
              </span>
            ) : (
              <span className="text-red-800 dark:text-red-400">
                Test failed: {testResult.error}
              </span>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4 border-t border-base-300">
          <button
            onClick={handleTest}
            disabled={isTestDisabled}
            className="px-4 py-2 border border-gray-300 dark:border-base-300 rounded-md text-gray-700 dark:text-base-content/80 hover:bg-base-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Testing..." : "Test Configuration"}
          </button>
          <button
            onClick={handleSave}
            disabled={isSaveDisabled}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Saving..." : "Save Configuration"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SSOConfiguration;
